import { useEffect, useState, useMemo, useReducer, Dispatch } from 'react';
import { useLocation } from 'react-router-dom';
import { Action as ReduxAction } from 'redux';
import { History } from 'history';
import { api } from '../../modules/common-utils-services/axiosUtil';
import { ITobaccoExemptResponse, IApplicantDetails, IDataToSubmit } from './types/apiResponse';
import { showModal } from '../../actions/common/actionCreators';
import { dashboardModalTypes } from '../../constants/individualPortal/dashboard/modals/dashboardModalTypes';
import { configs } from '../../constants/configs/configs';
import { environmentSelector } from '../../selectors/commonSelectors';
import { useAppSelector } from '../../hooks';
import { VALIDATION_TYPES } from '../../constants/inputs/validationTypes';
import { transferStatusTypes } from '../../constants/entity/modals/entityModalTypes';

interface IUseTobaccoExemptDataProps {
    history: History;
    dispatch: Dispatch<ReduxAction>;
}

export enum actions {
    FETCH_TOBACCO_EXEMPT_DATA = 'FETCH_TOBACCO_EXEMPT_DATA',
    FETCH_TOBACCO_EXEMPT_ERROR = 'FETCH_TOBACCO_EXEMPT_ERROR',
    FETCH_TOBACCO_EXEMPT_LOADING = 'FETCH_TOBACCO_EXEMPT_LOADING',
    SAVE_TOBACCO_EXEMPT_DATA = 'SAVE_TOBACCO_EXEMPT_DATA',
    SAVE_TOBACCO_EXEMPT_SUCCESS = 'SAVE_TOBACCO_EXEMPT_SUCCESS',
    SAVE_TOBACCO_EXEMPT_ERROR = 'SAVE_TOBACCO_EXEMPT_ERROR',
    UPDATE_TOBACCO_USE = 'UPDATE_TOBACCO_USE',
    UPDATE_HAS_DISABILITY = 'UPDATE_HAS_DISABILITY',
    UPDATE_EXEMPTION_NUMBER = 'UPDATE_EXEMPTION_NUMBER',
    UPDATE_HARD_EXEMPTION = 'UPDATE_HARD_EXEMPTION',
}

type Action =
    | { type: actions.FETCH_TOBACCO_EXEMPT_DATA; payload: ITobaccoExemptResponse }
    | { type: actions.FETCH_TOBACCO_EXEMPT_ERROR }
    | { type: actions.FETCH_TOBACCO_EXEMPT_LOADING }
    | { type: actions.SAVE_TOBACCO_EXEMPT_DATA }
    | { type: actions.SAVE_TOBACCO_EXEMPT_SUCCESS }
    | { type: actions.SAVE_TOBACCO_EXEMPT_ERROR }
    | { type: actions.UPDATE_TOBACCO_USE; payload: number }
    | { type: actions.UPDATE_HAS_DISABILITY; payload: number }
    | { type: actions.UPDATE_EXEMPTION_NUMBER; payload: { personId: number; value: string } }
    | { type: actions.UPDATE_HARD_EXEMPTION; payload: boolean };

function reducer(state, action: Action) {
    switch (action.type) {
        case actions.FETCH_TOBACCO_EXEMPT_LOADING:
            return { ...state, isAdditionalInfoLoading: true, isError: false };
        case actions.FETCH_TOBACCO_EXEMPT_DATA:
            return {
                ...state,
                additionalInfo: action.payload,
                isAdditionalInfoLoading: false,
                isError: false,
            };
        case actions.FETCH_TOBACCO_EXEMPT_ERROR:
            return {
                ...state,
                isAdditionalInfoLoading: false,
                isError: true,
            };
        case actions.SAVE_TOBACCO_EXEMPT_DATA:
            return {
                ...state,
                isSaving: true,
                isSavingError: false,
            };
        case actions.SAVE_TOBACCO_EXEMPT_SUCCESS:
            return {
                ...state,
                isSaving: false,
                isSavingError: false,
            };
        case actions.SAVE_TOBACCO_EXEMPT_ERROR:
            return {
                ...state,
                isSaving: false,
                isSavingError: true,
            };
        case actions.UPDATE_HARD_EXEMPTION:
            return {
                ...state,
                additionalInfo: {
                    ...state.additionalInfo,
                    householdExempt: action.payload,
                },
            };
        case actions.UPDATE_TOBACCO_USE:
            return {
                ...state,
                additionalInfo: {
                    ...state.additionalInfo,
                    applicantDetails: {
                        ...state.additionalInfo?.applicantDetails,
                        eligiblitydetails: state.additionalInfo?.applicantDetails?.eligiblitydetails.map(item =>
                            item.personId === action.payload ? { ...item, tobaccoUser: !item.tobaccoUser } : item,
                        ),
                    },
                },
            };
        case actions.UPDATE_HAS_DISABILITY:
            return {
                ...state,
                additionalInfo: {
                    ...state.additionalInfo,
                    applicantDetails: {
                        ...state.additionalInfo?.applicantDetails,
                        eligiblitydetails: state.additionalInfo?.applicantDetails?.eligiblitydetails.map(item =>
                            item.personId === action.payload
                                ? { ...item, blindOrDisabled: !item.blindOrDisabled }
                                : item,
                        ),
                    },
                },
            };
        case actions.UPDATE_EXEMPTION_NUMBER:
            return {
                ...state,
                additionalInfo: {
                    ...state.additionalInfo,
                    applicantDetails: {
                        ...state.additionalInfo?.applicantDetails,
                        eligiblitydetails: state.additionalInfo?.applicantDetails?.eligiblitydetails.map(
                            (item: IApplicantDetails) =>
                                item.personId === action.payload.personId
                                    ? { ...item, exemptionNumber: action.payload.value }
                                    : item,
                        ),
                    },
                },
            };
        default:
            return state;
    }
}

const initialState = {
    isError: false,
    isAdditionalInfoLoading: true,
    additionalInfo: null as ITobaccoExemptResponse | null,
    isSaving: false,
    isSavingError: false,
};
const REDIRECT_TO_CUSTOM_GROUPING = 'redirectToCustomGrouping';
const PLAN_SHOPPING_ID = 'planShoppingId';

export default ({ history, dispatch: reduxDispatch }: IUseTobaccoExemptDataProps) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const environment = useAppSelector(environmentSelector);
    const location = useLocation();
    const [caseNumber, setCaseNumber] = useState<string | null>(null);
    // Extract query parameter when the URL changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        setCaseNumber(params.get('caseNumber'));
    }, [location.search]);

    const { isState } = environment;
    const isHardshipExemptCheckBoxVisible = useMemo(
        () => state.additionalInfo?.showHardshipExemptCheckBox || isState.MN,
        [isState, state.additionalInfo?.showHardshipExemptCheckBox],
    );
    const isDisclaimerVisible = useMemo(() => isState.MN, [isState]);
    const getAdditionalInfo = async (): Promise<boolean> => {
        if (!caseNumber) {
            return false;
        }
        dispatch({ type: actions.FETCH_TOBACCO_EXEMPT_LOADING });
        try {
            const url = configs.endpoints.TOBACCO_EXEMPT_GET_APPLICANTS;
            let formData = new FormData();
            formData.append('caseNumber', caseNumber);

            const res = global.__ENV__.PRODUCTION
                ? await api.post<ITobaccoExemptResponse>(url, formData)
                : await api.get<ITobaccoExemptResponse>(url);

            const { data } = res;
            if (data) {
                dispatch({ type: actions.FETCH_TOBACCO_EXEMPT_DATA, payload: data });
            }
            return true;
        } catch (e) {
            console.log(
                `In Tobacco Exempt Page: getAdditionalInfo(): Request to (${configs.endpoints.ADDITIONAL_INFO}) failed with error = `,
                e,
            );
            dispatch({ type: actions.FETCH_TOBACCO_EXEMPT_ERROR });
            return false;
        }
    };
    const saveAdditionalInfo = async (dataToSubmit: IDataToSubmit): Promise<boolean> => {
        try {
            const url = configs.endpoints.TOBACCO_EXEMPT_SAVE_ADDINFO;
            const res = global.__ENV__.PRODUCTION ? await api.post(url, dataToSubmit) : await api.get(url);

            switch (res?.data?.message) {
                case REDIRECT_TO_CUSTOM_GROUPING:
                    history.push(configs.routes.CUSTOM_GROUPING);
                    return true;
                case PLAN_SHOPPING_ID:
                    const shoppingId = res.data.shoppingId;
                    window.location.href = configs.endpoints.PLAN_SHOPPING.processReplacements({ shoppingId });
                    return true;
                default:
                    break;
            }
            dispatch({ type: actions.SAVE_TOBACCO_EXEMPT_ERROR });
            return false;
        } catch (e) {
            dispatch({ type: actions.SAVE_TOBACCO_EXEMPT_ERROR });
            return false;
        }
    };
    const onSaveAndContinue = async (
        dataToSubmit: IDataToSubmit,
        isHardExemptionChecked: boolean,
        tableData: IApplicantDetails[],
    ): Promise<void> => {
        if (isHardExemptionChecked) {
            // Check if any applicant is missing an exemption number
            const allHaveExemptionNumber = tableData.every(
                applicant => applicant.exemptionNumber !== null && applicant.exemptionNumber !== '',
            );
            if (!allHaveExemptionNumber) {
                reduxDispatch(showModal({ modalData: {}, modalType: dashboardModalTypes.EXEMPTION_NUMBER_MODAL }));
                return;
            } else {
                await saveAdditionalInfo(dataToSubmit);
            }
        } else {
            const updatedDataToSubmit = {
                ...dataToSubmit,
                applicantDetails: {
                    ...dataToSubmit.applicantDetails,
                    eligiblitydetails: tableData.map(({ exemptionNumber, ...rest }) => rest),
                },
            };
            await saveAdditionalInfo(updatedDataToSubmit);
            return;
        }
    };
    const updateTobaccoUse = (personId: number) => {
        dispatch({ type: actions.UPDATE_TOBACCO_USE, payload: personId });
    };

    const updateHasDisability = (personId: number) => {
        dispatch({ type: actions.UPDATE_HAS_DISABILITY, payload: personId });
    };

    const updateExemptionNumber = (personId: number, value: string) => {
        dispatch({ type: actions.UPDATE_EXEMPTION_NUMBER, payload: { personId, value } });
    };

    const updateHardExemption = (isChecked: boolean) => {
        dispatch({ type: actions.UPDATE_HARD_EXEMPTION, payload: isChecked });
    };

    return {
        additionalInfoData: state,
        caseNumber,
        environment,
        getAdditionalInfo,
        saveAdditionalInfo,
        onSaveAndContinue,
        isDisclaimerVisible,
        isHardshipExemptCheckBoxVisible,
        isSavingError: state.isSavingError,
        updateTobaccoUse,
        updateExemptionNumber,
        updateHardExemption,
        updateHasDisability,
    };
};
